import React, { useEffect } from "react";
import ReactDOM from "react-dom";
import { observer } from "mobx-react";
import { useLocation } from "react-router-dom";
import SparkleIcon from "Components/common/SparkleIcon";
import copilotDrawerStore from "Stores/copilotDrawerStore";
import agentsPanelStore from "Stores/agentsPanelStore";
import userDetail from "Stores/userDetail";
import BotGlyph from "Pages/Dashboard/agents/BotGlyph";
import AgentsPanel from "./AgentsPanel";
import UpdatesPanel, { MegaphoneGlyph } from "./UpdatesPanel";
import updatesPanelStore, { REFRESH_EVERY_MS } from "Stores/updatesPanelStore";
import { currentSurface } from "Pages/Dashboard/Copilot/agent/copilotContext";
import Utils from "Utils/utils";
import { MXP_EVENT, PAGE_TYPE } from "Static/MixPanelEvents";
import { REQUEST_DEMO_CALENDAR_LINK } from "Static/constant";
import watchOverlays from "Utils/overlayDim";

/*
 * The right rail: a thin, always-present strip of app-level actions.
 *
 * It exists so the copilot has a permanent home. A button buried among the
 * header icons reads as one more tool; a rail that is always there, with the
 * panel opening beside it, reads as part of the application. Agents open the
 * same way (a docked panel), and Request Demo sits with them. Each item is an
 * icon over a label with a divider between, so the strip reads as a menu and
 * not as a row of unlabeled glyphs. Pricing lives in the left nav; onboarding
 * was dropped from here.
 */

const RAIL_CLASS = "sgHasRightRail";

const RightRail = observer(() => {
  // The full-page copilot is its own surface; a rail offering to open the
  // drawer on top of it would be two copilots arguing over one conversation.
  const onCopilotPage = useLocation().pathname.startsWith("/copilot");

  // The rail takes real width out of the page, and .mainContent is the element
  // that has to give it up -- same mechanism the pinned drawer uses.
  useEffect(() => {
    const root = document.body;
    root.classList.add(RAIL_CLASS);
    const stopWatchingOverlays = watchOverlays();
    // Check for unread updates once the page has settled, off the boot path.
    const loadUpdates = setTimeout(updatesPanelStore.load, 4000);
    const refreshUpdates = setInterval(updatesPanelStore.refresh, REFRESH_EVERY_MS);
    window.addEventListener("focus", updatesPanelStore.refresh);
    return () => {
      root.classList.remove(RAIL_CLASS);
      stopWatchingOverlays();
      clearTimeout(loadUpdates);
      clearInterval(refreshUpdates);
      window.removeEventListener("focus", updatesPanelStore.refresh);
    };
  }, []);

  const track = event => Utils.mixpanelTrack(event, { pageType: PAGE_TYPE.header });

  const { agentsEnabled = false } = userDetail?.userFeatureFlag || {};

  // Copilot, agents and what's new share the slot beside the rail: opening one closes the others.
  const toggleCopilot = () => {
    const { surface, entityId } = currentSurface();
    agentsPanelStore.close();
    updatesPanelStore.close();
    copilotDrawerStore.toggle(surface, entityId);
  };

  const toggleAgents = () => {
    if (!agentsPanelStore.open) copilotDrawerStore.close();
    updatesPanelStore.close();
    agentsPanelStore.toggle();
  };

  const toggleUpdates = () => {
    if (!updatesPanelStore.open) copilotDrawerStore.close();
    agentsPanelStore.close();
    updatesPanelStore.toggle();
  };

  // Portalled to <body> for the same reason the drawer is: .mainContent sets a
  // transform, which makes it the containing block for its fixed descendants --
  // so a rail rendered inside it would pin to the right edge of the PAGE AREA
  // (already narrowed by the drawer) instead of the viewport.
  return (
    <>
      {ReactDOM.createPortal(
        <nav className="sgRightRail" aria-label="Assistant and product">
          {!onCopilotPage && (
            <button
              type="button"
              className={`sgRailBtn sgRailBtn--copilot${copilotDrawerStore.open ? " isOn" : ""}`}
              aria-pressed={copilotDrawerStore.open}
              onClick={toggleCopilot}
            >
              <SparkleIcon />
              <span className="sgRailBtn__label">Copilot</span>
            </button>
          )}

          {agentsEnabled && (
            <>
              {!onCopilotPage && <span className="sgRailDivider" aria-hidden="true" />}
              <button
                type="button"
                className={`sgRailBtn${agentsPanelStore.open ? " isOn" : ""}`}
                aria-pressed={agentsPanelStore.open}
                onClick={toggleAgents}
              >
                <BotGlyph size={19} />
                <span className="sgRailBtn__label">Agents</span>
              </button>
            </>
          )}

          <span className="sgRailDivider" aria-hidden="true" />

          <button
            type="button"
            className={`sgRailBtn sgRailBtn--updates${updatesPanelStore.open ? " isOn" : ""}`}
            aria-pressed={updatesPanelStore.open}
            aria-label={
              updatesPanelStore.unseenCount
                ? `What's new, ${updatesPanelStore.unseenCount} unread`
                : "What's new"
            }
            onClick={toggleUpdates}
          >
            <MegaphoneGlyph />
            {updatesPanelStore.unseenCount > 0 && (
              <span className="sgRailBtn__count" aria-hidden="true">
                {updatesPanelStore.unseenCount > 9 ? "9+" : updatesPanelStore.unseenCount}
              </span>
            )}
            <span className="sgRailBtn__label">Updates</span>
          </button>

          <span className="sgRailDivider" aria-hidden="true" />

          <button
            type="button"
            className="sgRailBtn"
            onClick={() => {
              track(MXP_EVENT.REQUEST_DEMO_HEADER);
              Utils.windowRedirect(REQUEST_DEMO_CALENDAR_LINK, "_blank", true);
            }}
          >
            <i className="material-icons-outlined">calendar_today</i>
            <span className="sgRailBtn__label">Demo</span>
          </button>
        </nav>,
        document.body
      )}
      {agentsEnabled && <AgentsPanel />}
      <UpdatesPanel />
    </>
  );
});

export default RightRail;
